import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, Clock, Phone, Navigation, Zap, Award, MapPin, Check } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { PageHeader, FullPageSpinner } from "@/components/kit";
import { trackVet, mapsUrl } from "@/components/VetCard";

export default function VetDetail() {
  const { id } = useParams();
  const [vet, setVet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const v = await apiFetch(`/vets/${id}`, {}, false);
        setVet(v);
        trackVet(id, "view");
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <FullPageSpinner />;
  if (!vet) return <PageHeader title="Vet not found" backTo="/app/vets" />;

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader title={vet.name} backTo="/app/vets" />

      <div className="rounded-3xl overflow-hidden border border-[#E8E3DA] bg-white">
        <div className="relative h-56">
          <img src={vet.photo_url} alt={vet.name} className="h-full w-full object-cover" />
          <div className="absolute top-3 left-3 flex gap-2">
            {vet.is_featured && (
              <span className="inline-flex items-center gap-1 text-xs font-extrabold uppercase px-3 py-1 rounded-full bg-[#E86A45] text-white">
                <Award size={13} /> Featured
              </span>
            )}
            {vet.is_emergency_24_7 && (
              <span className="inline-flex items-center gap-1 text-xs font-extrabold uppercase px-3 py-1 rounded-full bg-[#D9534F] text-white">
                <Zap size={13} /> 24/7 Emergency
              </span>
            )}
          </div>
        </div>

        <div className="p-5">
          <div className="flex items-center gap-3 text-[#4A4746]">
            <span className="flex items-center gap-1 font-bold text-[#2D2B2A]">
              <Star size={16} color="#F3B05A" fill="#F3B05A" /> {vet.rating}
            </span>
            <span className="text-sm">({vet.reviews_count} reviews)</span>
            <span>·</span>
            <span className="text-sm">{vet.distance_km?.toFixed?.(1) ?? vet.distance_km} km away</span>
          </div>

          <div className="mt-4 space-y-2 text-[#4A4746]">
            <p className="flex items-start gap-2"><MapPin size={18} className="mt-0.5 shrink-0" /> {vet.address}, {vet.city}</p>
            <p className="flex items-center gap-2"><Clock size={18} /> {vet.hours}</p>
          </div>

          {vet.services?.length > 0 && (
            <div className="mt-5">
              <p className="font-extrabold mb-2">Services</p>
              <div className="flex flex-wrap gap-2">
                {vet.services.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-[#F2EFE9] font-semibold text-[#4A4746]">
                    <Check size={14} color="#6F8F72" /> {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mt-6">
            <a
              href={`tel:${vet.phone}`}
              data-testid="vet-call-btn"
              onClick={() => trackVet(vet.id, "call")}
              className="flex items-center justify-center gap-2 rounded-full bg-[#E86A45] text-white font-extrabold py-3 hover:bg-[#d65f3c] transition-colors"
            >
              <Phone size={18} /> Call
            </a>
            <a
              href={mapsUrl(vet)}
              target="_blank"
              rel="noreferrer"
              data-testid="vet-directions-btn"
              onClick={() => trackVet(vet.id, "directions")}
              className="flex items-center justify-center gap-2 rounded-full border-2 border-[#E8E3DA] font-extrabold py-3 hover:border-[#C4BFB8] transition-colors"
            >
              <Navigation size={18} /> Directions
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
